import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useKeycloakToken } from 'components/common/useKeycloak';
import RouteDetails from './RouteDetails';
import {
  addRouteDestinations,
  createRoute,
  deleteRoute,
  deleteRouteConfiguration,
  getRoute,
  getRouteApiKey,
  getRouteDetails,
  getIntegrationDetails,
  getReverseRoutes,
  getRouteConfiguration,
  renameRoute,
  testConnectionAction,
  updateConnection,
  updateRouteConfiguration,
} from 'api/routes/routesApi';
import { routeDisplayName } from './routeDisplayName';
import { routeRecordId } from './routeDisplayName';
import { FlowDirectionKey } from './flowMap/flowMapTypes';
import { resolveRulesTarget } from './flowMap/rulesTarget';
import TransformationRulesPanel from './transformationRules/TransformationRulesPanel';
import UnsavedSectionPrompt, {
  PendingEdits,
  SectionPrompt,
} from 'components/common/configPanel/UnsavedSectionPrompt';
import {
  SourceFilter,
  SourceFilterDraft,
  SourceSummary,
  sourceFilterKey,
} from './sourceFilters/sourceFilterTypes';
import {
  createRouteFilter,
  deleteRouteFilter,
  getRouteFilterSources,
  listRouteFilters,
  updateRouteFilter,
} from 'api/routes/routeFiltersApi';
import {
  FieldErrors,
  FieldMapping,
  FieldMappingsConfig,
  STREAM_TYPES,
  StreamType,
  buildConfigWithRule,
  buildConfigWithoutRule,
  getRuleForDestination,
  parseServerErrors,
} from './transformationRules/fieldMappingTypes';
import {
  Route,
  ConnectionConfiguration,
  Destination,
  Member,
  Organization,
  Provider,
  Source,
  buildRoute,
  buildDestination,
  buildMember,
  buildProvider,
} from 'components/common/ObjectTypes';
import { getDestinationsList } from 'api/destinations/destinationsApi';
import { createSource, getSourcesForRoute } from 'api/sources/sourcesApi';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Ajv from 'ajv';
import addFormats from 'ajv-formats';
import addKeywords from 'ajv-keywords';
import { useWorkspacePicker } from 'api/organizations/useAllOrganizations';
import { providerRoutesQuery } from 'api/routes/providerRoutesQuery';
import { page_size, SessionExpiredError } from 'api/apiUtils';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouteLogsTab } from './useRouteLogsTab';
import { queryKeys } from 'api/queryKeys';
import {
  destinationsNeedAuthEnrichment,
  enrichDestinationsWithAuth,
} from 'components/destinations/DestinationHelperFunctions';
import { changeSourceGlobalSearch } from 'components/sources/SourceHelperFunctions';
import { useDestinationUrls } from 'api/useFilterOptions';
import { useListQuery } from 'api/useListQuery';
import RouteDetailsSource from './RouteDetailsSources';
import RouteDetailsLog from './RouteDetailsLog';
import CancelChangesConfirmationModal from './CancelChangesConfirmationModal';
import getUserPermissions from 'api/users/getUserPermissions';
import { useTranslation } from 'react-i18next';
import { useHelp, HelpCard } from 'context/HelpContext';
import {
  getRouteKind,
  providerHasPushAction,
} from 'components/common/routeKind';
import { useKeycloak } from '@react-keycloak/web';
import {
  trackRouteEnabled,
  trackRouteDisabled,
  trackDestinationAdded,
} from 'analytics/analytics';
import { getMyDetails } from 'api/users/usersApi';
import { loadCurrentUserDetails } from 'components/organizations/loadCurrentUserDetails';
import RouteProgressIndicator, {
  StepStatus,
} from './NewRouteProgressIndicator/RouteProgressIndicator';
import { INREACH_TYPE } from 'components/common/configPanel/tempExemptions';
import { reverseRouteName } from './hideReverseRoutes';
import {
  buttonClasses,
  inputClasses,
  skeletonClasses,
  routeDetailsClasses,
} from 'components/common/tailwindClassnames';
import SelectInput from 'components/common/SelectInput';
import NotFoundPage from 'components/common/NotFoundPage';
import { isValidRouteSubpage, routeTabPath } from './routeSubpages';
import ConfigurationsTab from './flowMap/ConfigurationsTab';
import AddDestinationDrawer from './flowMap/AddDestinationDrawer';

type ReverseConfigBySite = Record<
  string,
  { routeId: string; name: string; data: FieldMappingsConfig }
>;
// Stable empties for data that hasn't loaded yet, so memos and effects that
// depend on them don't re-run on every render.
const EMPTY_FIELD_MAPPINGS: FieldMappingsConfig = { field_mappings: {} };
const EMPTY_REVERSE_MAP: Record<string, string[]> = {};
const EMPTY_SHARED: { id: string; name: string }[] = [];
const EMPTY_REVERSE_CONFIGS: ReverseConfigBySite = {};

const GENERAL_TAB_SKELETON_ROWS = [
  { label: 'w-32', desc: 'w-52' },
  { label: 'w-24', desc: 'w-44' },
];

export function ManageRouteDetails() {
  type FilterTags = {
    name: string;
    id: string;
  };
  type FilterTypes = {
    base_url__in: FilterTags[];
    enabled: FilterTags[];
    type__in: FilterTags[];
    owner__in: FilterTags[];
    ordering: string;
    search: string;
  };
  type SourceFilterTypes = {
    destination_url__in: FilterTags[];
    enabled: FilterTags[];
    type__in: FilterTags[];
    owner__in: FilterTags[];
    ordering: string;
    search: string;
  };

  const { t, i18n } = useTranslation();
  const {
    setHelpContent,
    closePanel: closeHelpPanel,
    setRightOffsetPx: setHelpOffsetPx,
  } = useHelp();
  const { route_id, subpage } = useParams();
  const queryClient = useQueryClient();
  // The :subpage route param accepts any string — an unknown value renders no
  // tab. Validate the param itself, not the resolved tab: an absent subpage is
  // valid and resolves below, once the Route's health is known.
  const isValidSubpage = isValidRouteSubpage(subpage);
  const [token, showLoginModal, setShowLoginModal] = useKeycloakToken();
  const { keycloak, initialized } = useKeycloak();
  const location = useLocation();
  const [currentMemberIsAdmin, setCurrentMemberIsAdmin] =
    useState<boolean>(false);
  const [, setIsFromAddRouteFlow] = useState(false);
  const [canAddDestination, setCanAddDestination] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [route, setRoute] = useState<Route>(buildRoute());
  // A Route has exactly three addresses: /routes/:id, /routes/:id/sources and
  // /routes/:id/logs. The bare one IS the Configurations tab. An unhealthy
  // Route opens on Logs instead, but that happens once, as a redirect on
  // arrival (see below) — deriving it here on every render made Configurations
  // unreachable, because the tab strip sends Configurations back to the bare
  // URL and this would immediately re-answer "logs".
  const effectiveSubpage = subpage || 'configurations';
  // The Route's own (editable) name — decoupled from the provider Connection's
  // name, seeded from the route record whenever the Route is (re)fetched.
  const [routeName, setRouteName] = useState('');
  // Some Routes come back with default_route: null even though they have a
  // route in routing_rules (seen on stage, e.g. ad1e98d7…). Everything that
  // PATCHes the route (destinations list, field mappings) needs the route id,
  // so fall back to the Route's first routing rule.
  const defaultRouteId: string | undefined = routeRecordId(route);
  const [routeHasDestinations, setRouteHasDestinations] = useState(
    () => route.destinations.length > 0,
  );
  // Reported by the Configurations tab: accounts and flows whose required fields
  // are still empty. null until it has loaded them.
  const [pendingSetup, setPendingSetup] = useState<{
    total: number;
    provider: boolean;
    destinations: boolean;
  } | null>(null);
  // Reloading the Route after a save refetches it; the effect that receives
  // the result copies it into the page's editable state.
  const requestRouteReload = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.routes.detail(route_id ?? ''),
      }),
    [queryClient, route_id],
  );
  const [routeProvider, setRouteProvider] = useState<Provider>(buildProvider());
  const [destinationFilter, setDestinationFilter] = useState<FilterTypes>({
    base_url__in: [],
    enabled: [],
    type__in: [],
    owner__in: [],
    ordering: '',
    search: '',
  });
  const [sourceFilter, setSourceFilter] = useState<SourceFilterTypes>(() => {
    const savedFilter = sessionStorage.getItem('routeSourceFilter');
    return savedFilter
      ? JSON.parse(savedFilter)
      : {
          destination_url__in: [],
          enabled: [],
          type__in: [],
          owner__in: [],
          // Alphabetical by default, matching the device-filter editor's list.
          ordering: 'external_id',
          search: '',
        };
  });
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  /* 
        ROUTE DESTINATIONS:
            - "destinations" array is for destinations already associated with the route
            - "existingDestPhase0" is for existing destinations that are available to add and unselected
            regardless of search/filter results
    */
  const [destinations, setDestinations] = useState<Destination[]>([]);

  // Transformation Rules side panel state
  const [rulesPanelOpen, setRulesPanelOpen] = useState(false);
  // Which rule's editor the panel opens on; null only before the first open.
  const [rulesInitialEditor, setRulesInitialEditor] = useState<
    'device' | 'field_mapping' | null
  >(null);
  // Bumped on every open request, so Edit on another rule's card reaches the editor
  // even when the panel is already open (isOpen alone has no edge to fire on then).
  const [rulesEditorNonce, setRulesEditorNonce] = useState(0);
  const [addDestinationDrawerOpen, setAddDestinationDrawerOpen] =
    useState(false);
  // The drawer refetches its list on open, but that effect runs after the first
  // paint — opening with the previous rows still in state flashed them for a
  // frame, and a click could land on a row about to be replaced by the fetch.
  // Opening through here shows the loading skeleton from the very first frame.
  // The drawer opens scoped to the Route's workspace with no search or
  // technology filter, set in the same update as opening it so its first
  // request already carries them.
  const openAddDestinationDrawer = () => {
    setDrawerWorkspaceFilter(
      route.owner?.id
        ? { label: route.owner.name, value: route.owner.id }
        : null,
    );
    setDestinationFilter((prev: FilterTypes) => ({
      ...prev,
      search: '',
      type__in: [],
      owner__in: route.owner?.id
        ? [{ name: route.owner.name, id: route.owner.id }]
        : [],
    }));
    setDrawerOffset(0);
    setAddDestinationDrawerOpen(true);
  };
  // The drawer's page — the same numbered pagination the Routes list uses.
  const [drawerOffset, setDrawerOffset] = useState(0);
  // Drawer workspace filter — defaults to the Route's workspace; clearing
  // it lists destinations from every workspace the user can see.
  const [drawerWorkspaceFilter, setDrawerWorkspaceFilter] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const [rulesPanelDestination, setRulesPanelDestination] =
    useState<Destination | null>(null);
  // Which arrow the rules panel was opened from. "out" = this Route (provider →
  // site); "in" = the reverse Route (site → provider), whose field mappings live
  // on that other route record.
  const [rulesDirection, setRulesDirection] = useState<FlowDirectionKey>('out');
  // Field mappings of the reverse Route of each site, keyed by site id. The
  // reverse direction is a separate route record, so its configuration is a
  // separate fetch — without it the "in" arrow could only ever show "no rule".
  // The route_configuration's NAME, cached separately from field_mappings: a
  // PATCH replaces the whole configuration payload, so every save has to resend
  // it. Its id is not tracked — the backend resolves the configuration from the
  // route, and a route id is what the DELETE addresses.

  useEffect(() => {
    setRouteHasDestinations(destinations.length > 0);
  }, [destinations]);

  // The Logs tab pins its filter under the nav by scrolling the page down. That
  // scroll offset is the window's, not the tab's, and a tab switch is a route
  // change — which never resets it — so every other tab used to open already
  // scrolled past its own header. Reset on each tab change. Keyed on the subpage
  // and not on the location: the Configurations panel navigates within
  // "configurations" (account/flow selection) and must keep the user's place.
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [effectiveSubpage]);

  useEffect(() => {
    const providerType = routeProvider.type;

    // The Route-rules guide is tied to the Rules panel, not the provider type, so
    // it's built independently of the provider-type guard below.
    const rulesCards: HelpCard[] = rulesPanelOpen
      ? [
          {
            title: t('Route Rules'),
            description: t(
              'Learn how to transform and map data with Route rules.',
            ),
            button: {
              label: t('Learn More'),
              url: 'https://support.earthranger.com/routes-gundi/route-rules',
            },
          },
        ]
      : [];

    if (!providerType?.id || !providerType?.value) {
      setHelpContent(rulesCards.length > 0 ? { cards: rulesCards } : null);
      return;
    }

    const kind = getRouteKind(providerType);
    const cards: HelpCard[] = [
      // While the Rules side panel is open, surface the Route-rules guide first.
      ...rulesCards,
      // The flow map is the page's main content and a new idea, so it gets a
      // card of its own wherever it is on screen.
      ...(effectiveSubpage === 'configurations'
        ? [
            {
              title: t('Configuring a Route'),
              description: t(
                'See how data moves between the systems in this Route, and set up each direction.',
              ),
              button: {
                label: t('Learn More'),
                url: 'https://support.earthranger.com/routes-gundi/routes',
              },
            },
            {
              title: t('Add a Destination to a Route'),
              description: t(
                'Learn the three ways to add a destination to a Route.',
              ),
              button: {
                label: t('Learn More'),
                url: 'https://support.earthranger.com/routes-gundi/add-destination-to-route',
              },
            },
          ]
        : []),
      ...(kind === 'webhook'
        ? [
            {
              title: t('Gundi Webhook API'),
              description: t('Learn how to send data to Gundi using webhooks.'),
              button: {
                label: t('Learn More'),
                url: 'https://support.earthranger.com/developer_docs/custom-webhook',
              },
            },
          ]
        : []),
      ...(kind === 'push-only' || providerHasPushAction(providerType)
        ? [
            {
              title: t('Gundi API'),
              description: t('Learn how to push data to Gundi using the API.'),
              button: {
                label: t('Learn More'),
                url: 'https://support.earthranger.com/developer_docs/gundi-api',
              },
            },
          ]
        : []),
    ];
    setHelpContent(cards.length > 0 ? { cards } : null);
  }, [
    routeProvider.type,
    rulesPanelOpen,
    effectiveSubpage,
    i18n.language,
    t,
    setHelpContent,
  ]);

  useEffect(() => {
    return () => setHelpContent(null);
  }, [setHelpContent]);

  // Coordinate the Help side panel with the Rules side panel across three width ranges:
  //   >= 1400px : both coexist side-by-side — shift Help + "?" left by the Rules width.
  //   768-1400  : not enough room for both, so close Help when Rules opens (see effect below).
  //   < 768px   : the md/hamburger breakpoint — Rules is full-screen and Help stacks
  //               full-screen on top of it, so there's nothing to shift (offset stays 0).
  // The offset is recomputed on resize so dragging across a breakpoint stays consistent.
  useEffect(() => {
    const RULES_PANEL_WIDTH = 520;
    const FULLSCREEN_MAX_WIDTH = 768;
    const applyOffset = () => {
      if (!rulesPanelOpen) {
        setHelpOffsetPx(0);
        return;
      }
      const width =
        typeof window !== 'undefined' ? window.innerWidth : RULES_PANEL_WIDTH;
      setHelpOffsetPx(width < FULLSCREEN_MAX_WIDTH ? 0 : RULES_PANEL_WIDTH);
    };
    applyOffset();
    window.addEventListener('resize', applyOffset);
    return () => {
      window.removeEventListener('resize', applyOffset);
      setHelpOffsetPx(0);
    };
  }, [rulesPanelOpen, setHelpOffsetPx]);

  // One-shot when Rules opens: in the mid range Help can't sit beside it, so close Help and
  // let the user re-open it from the (shifted) "?". Below the full-screen breakpoint they
  // stack instead, so Help is left alone there.
  useEffect(() => {
    if (!rulesPanelOpen) return;
    const COEXIST_MIN_WIDTH = 1400;
    const FULLSCREEN_MAX_WIDTH = 768;
    const width =
      typeof window !== 'undefined' ? window.innerWidth : COEXIST_MIN_WIDTH;
    if (width >= FULLSCREEN_MAX_WIDTH && width < COEXIST_MIN_WIDTH) {
      closeHelpPanel();
    }
  }, [rulesPanelOpen, closeHelpPanel]);

  const {
    organizations,
    loadingOrganizations,
    searchWorkspaces,
    clearWorkspaceSearch,
  } = useWorkspacePicker({ enabled: pageLoaded });

  const [sourcesOffset, setSourcesOffset] = useState<number>(0);
  // The Sources tab loads the first time it's opened, then follows its filters.
  const [sourcesTabOpened, setSourcesTabOpened] = useState(false);
  const routeSourcesQuery = useMemo(
    () => ({
      search: sourceFilter.search,
      destination_url__in: sourceFilter.destination_url__in
        .map((a) => a.id)
        .toString(),
      ordering: sourceFilter.ordering,
      offset: sourcesOffset,
      previous:
        process.env.REACT_APP_BASE_URL_LOCAL +
        '/sources/?pager=limit&limit=' +
        page_size +
        '&offset=' +
        (sourcesOffset - page_size),
      next:
        process.env.REACT_APP_BASE_URL_LOCAL +
        '/sources/?pager=limit&limit=' +
        page_size +
        '&offset=' +
        (sourcesOffset + page_size),
    }),
    [sourceFilter, sourcesOffset],
  );
  const routeSources = useListQuery<Source>(
    queryKeys.routes.sources(
      route_id ?? '',
      routeProvider.id ?? '',
      routeSourcesQuery,
    ),
    (token) => getSourcesForRoute(token, routeProvider.id, routeSourcesQuery),
    pageLoaded && sourcesTabOpened && !!routeProvider.id,
  );

  // Waits for the Route itself so the filter options don't compete with it.
  const { destinationUrls, loadingDestinationUrls } = useDestinationUrls({
    enabled: pageLoaded,
  });

  const [changesMade, setChangesMade] = useState(false);
  // Remove addDestModal state, not needed for page navigation
  const [errors, setErrors] = useState({});
  const [testActionLoading, setTestActionLoading] = useState(false);
  const [testActionData, setTestActionData] = useState<any>(null);
  const navigate = useNavigate();

  useEffect(() => {
    sessionStorage.setItem('routeSourceFilter', JSON.stringify(sourceFilter));
  }, [sourceFilter]);

  useEffect(() => {
    const loadPerms = async () => {
      if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
        try {
          const temp = await getUserPermissions(token);
          setCanAddDestination(temp);
        } catch (err) {
          console.log(err);
        }
      }
    };

    // An invalid subpage renders the 404 page — skip the fetch cascade
    if (!pageLoaded && isValidSubpage) {
      loadPerms();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, pageLoaded, isValidSubpage]);

  // The Route, plus the two things the page needs before it can show it:
  // whether you administer its workspace, and its provider integration.
  // An invalid subpage renders the 404 page, so nothing is fetched for it.
  const isLoggedIn =
    !!keycloak?.authenticated &&
    localStorage.getItem('userAuthorized') !== null;
  const routeQuery = useQuery({
    queryKey: queryKeys.routes.detail(route_id ?? ''),
    // Not kept after leaving the page: a cached copy would fill the form on
    // the next visit and the refetch would then overwrite what the user
    // started typing. Saves still refresh it in place.
    gcTime: 0,
    enabled:
      isValidSubpage &&
      !!route_id &&
      isLoggedIn &&
      !!token &&
      typeof token === 'string' &&
      !keycloak.isTokenExpired(),
    queryFn: async () => {
      const tempRoute = await fetchRouteScoped(token as string);
      if (!tempRoute) return null;
      const [isAdmin, provider] = await Promise.all([
        loadCurrentUserDetails(tempRoute.owner.id, token as string),
        getIntegrationDetails(token as string, tempRoute.provider.id),
      ]);
      return {
        route: tempRoute,
        isAdmin: !!isAdmin,
        provider: provider as Provider,
      };
    },
  });

  useEffect(() => {
    const result = routeQuery.data;
    if (result === undefined) return;
    if (result === null) {
      // A swallowed 401 also resolves undefined. That is an expired
      // session (the login modal is already up), not a missing
      // Route — painting a 404 under it would strand the user on a
      // false negative after logging back in.
      if (!keycloak.isTokenExpired()) setNotFound(true);
      return;
    }
    const tempRoute = result.route;
    setRoute(tempRoute);
    // Add the Route's workspace only if it's missing: this runs again after
    // every save, and toggling would drop the filter the user set.
    setDestinationFilter((prev: FilterTypes) =>
      prev.owner__in.some((tag: any) => tag.id === tempRoute.owner.id)
        ? prev
        : {
            ...prev,
            owner__in: [
              ...prev.owner__in,
              { name: tempRoute.owner.name, id: tempRoute.owner.id },
            ],
          },
    );
    setCurrentMemberIsAdmin(result.isAdmin);
    // Reset only on success: an eager reset flashes the loading state over
    // the 404 page while a retry runs.
    setNotFound(false);
    setPageLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeQuery.data, routeQuery.dataUpdatedAt]);

  // The page's copy of the Route: name, provider and destinations. It waits
  // out a refresh in flight, so a save never briefly shows the pre-save
  // values. Destinations and the provider always follow the server — the
  // attach/remove and provider saves are built from them, and a stale copy
  // would send an old list or undo a change. Only the real drafts are held
  // back while unsaved: the name, the workspace picked for the provider and
  // the webhook settings being edited.
  useEffect(() => {
    const result = routeQuery.data;
    if (!result || routeQuery.isFetching) return;
    setDestinations(result.route.destinations);
    setRouteHasDestinations(result.route.destinations.length > 0);
    if (!changesMade) {
      setRouteName(routeDisplayName(result.route));
      setRouteProvider(result.provider);
      return;
    }
    setRouteProvider((draft: Provider) =>
      draft.id !== result.provider.id
        ? result.provider
        : {
            ...result.provider,
            owner: draft.owner,
            webhook_configuration: draft.webhook_configuration,
          },
    );
  }, [
    routeQuery.data,
    routeQuery.dataUpdatedAt,
    changesMade,
    routeQuery.isFetching,
  ]);

  useEffect(() => {
    if (!routeQuery.error) return;
    // 403 is treated like 404: the entity is not viewable by this user.
    // Anything else (500, offline) is not a 404; the page stays as it is.
    const status = (routeQuery.error as any)?.response?.status;
    if (status === 404 || status === 403) setNotFound(true);
    else console.log('Failed to load route details:', routeQuery.error);
  }, [routeQuery.error, routeQuery.errorUpdatedAt]);

  const logsTab = useRouteLogsTab({
    routeId: route_id ?? '',
    providerId: route.provider?.id,
    destinationIds: destinations.map((d) => d.id),
    active: effectiveSubpage === 'logs',
    loadedRoute: routeQuery.data
      ? {
          status: routeQuery.data.route.status,
          loadedAt: routeQuery.dataUpdatedAt,
        }
      : null,
  });

  // Opening the drawer always fetches its list fresh (the cached copy is
  // stale by then); it waits for the Route so its workspace filter is set.
  const drawerQuery = useMemo(
    () => ({
      base_url__in: destinationFilter.base_url__in.map((a) => a.id).toString(),
      enabled: destinationFilter.enabled.map((a) => a.id).toString(),
      type__in: destinationFilter.type__in.map((a) => a.id).toString(),
      owner__in: destinationFilter.owner__in.map((a) => a.id).toString(),
      ordering: destinationFilter.ordering,
      search: destinationFilter.search,
      offset: drawerOffset,
      previous:
        process.env.REACT_APP_BASE_URL_LOCAL +
        '/integrations/?pager=limit&limit=' +
        page_size +
        '&offset=' +
        (drawerOffset - page_size),
      next:
        process.env.REACT_APP_BASE_URL_LOCAL +
        '/integrations/?pager=limit&limit=' +
        page_size +
        '&offset=' +
        (drawerOffset + page_size),
    }),
    [destinationFilter, drawerOffset],
  );
  const drawerList = useListQuery<Destination>(
    queryKeys.connections.drawer(drawerQuery),
    (token) => getDestinationsList(token, drawerQuery),
    addDestinationDrawerOpen && pageLoaded,
  );
  // EarthRanger rows in the drawer show which ER account they use (username or
  // truncated token) — the list endpoint omits configurations, so enrich them.
  // The plain rows show first; the enriched ones replace them when they land.
  const drawerAuth = useQuery({
    queryKey: queryKeys.connections.auth(drawerList.results.map((d) => d.id)),
    queryFn: () =>
      enrichDestinationsWithAuth(token as string, drawerList.results),
    enabled:
      addDestinationDrawerOpen &&
      destinationsNeedAuthEnrichment(drawerList.results) &&
      !!token &&
      typeof token === 'string' &&
      !keycloak.isTokenExpired(),
  });
  // What's already attached to this Route can't be attached again, and the
  // Route's own provider (now listed as a destination too) would point the
  // Route at itself.
  const drawerCandidates = useMemo(() => {
    const attached = new Set([
      ...destinations.map((d) => d.id),
      routeProvider.id,
    ]);
    return (drawerAuth.data ?? drawerList.results).filter(
      (d) => !attached.has(d.id),
    );
  }, [drawerAuth.data, drawerList.results, destinations, routeProvider.id]);

  const handleDrawerWorkspaceChange = (
    option: { label: string; value: string } | null,
  ) => {
    setDrawerWorkspaceFilter(option);
    setDestinationFilter((prev: FilterTypes) => ({
      ...prev,
      owner__in: option ? [{ name: option.label, id: option.value }] : [],
    }));
    setDrawerOffset(0);
  };

  useEffect(() => {
    if (!pageLoaded) return;
    if (effectiveSubpage === 'sources') setSourcesTabOpened(true);
  }, [pageLoaded, effectiveSubpage]);

  useEffect(() => {
    if (location.state && location.state.fromAddRouteFlow) {
      setIsFromAddRouteFlow(true);
    }
  }, [location, subpage]);

  // LOAD FUNCTIONS
  // The URL carries the provider INTEGRATION id, not the route record id: the
  // integration id is what backend logs carry and what populates the source
  // provider id in EarthRanger, so a portal link can be correlated with a log
  // line on its own. Resolve the provider's connection first, then scope down
  // to its own route record — the page's `route` object keeps the connection
  // shape downstream code expects, but its name and destinations are THIS
  // route's, not the union across every route the provider feeds.
  const fetchRouteScoped = async (tkn: string) => {
    const conn = await getRouteDetails(tkn, route_id || '');
    if (!conn || !conn.owner?.id) return null;
    const recordId = routeRecordId(conn);
    // An integration with no route record has a Connection page but no Route
    // page, so the caller's 404 is the right answer. Returning the connection
    // unscoped is not an acceptable fallback: its `destinations` is the union
    // across ALL of the provider's routes, and the PATCHes on this page
    // target ONE record — presenting the union would let a save graft
    // another route's destinations on. Better to fail the load than to show
    // editable data that lies. A swallowed 401 also lands here, and there the
    // login modal is the right outcome.
    if (!recordId) return null;
    const own = await getRoute(tkn, recordId).catch((err: any) => {
      // 404/403 mean the record is gone or not visible to this user, which
      // is a real 404 for this page. Anything else — a 500, an offline
      // browser — is a load failure, and painting "this Route does not
      // exist" over a transient backend error tells the user something
      // false. Let it through; loadRouteDetails settles it without a 404.
      const status = err?.response?.status;
      if (status === 404 || status === 403) return null;
      throw err;
    });
    if (!own) return null;
    return {
      ...conn,
      destinations: own.destinations ?? [],
      default_route: { id: own.id, name: own.name },
    };
  };

  const handleDestinationsPageClick = (e: any) => {
    setDrawerOffset(e.selected * page_size);
  };

  // This Route's own field mappings. Keyed by the route RECORD id the PATCHes
  // target — never the page's :route_id URL param, which is the provider
  // integration id.
  const routeConfigQuery = useQuery({
    queryKey: queryKeys.routes.configuration(defaultRouteId ?? ''),
    queryFn: async () => {
      const record = await getRouteConfiguration(
        token as string,
        defaultRouteId as string,
      );
      // Undefined means the session expired (handleError swallowed the
      // 401). Never store that as an empty configuration: a save built
      // from it would PATCH every destination's real mappings away.
      if (record === undefined) throw new SessionExpiredError();
      return {
        data: (record?.configuration?.data ?? {
          field_mappings: {},
        }) as FieldMappingsConfig,
        name: (record?.configuration?.name ?? '') as string,
      };
    },
    enabled:
      pageLoaded &&
      !!defaultRouteId &&
      !!token &&
      typeof token === 'string' &&
      !keycloak.isTokenExpired(),
  });
  const routeConfigData: FieldMappingsConfig =
    routeConfigQuery.data?.data ?? EMPTY_FIELD_MAPPINGS;
  const routeConfigurationName = routeConfigQuery.data?.name ?? '';

  const tokenUsable =
    !!token && typeof token === 'string' && !keycloak.isTokenExpired();
  // The other Routes this same provider integration feeds. `enabled` is a field
  // on that integration, so disabling from one Route's map stops all of them —
  // this is what the confirmation has to name. Distinct from
  // providerSharedWith, which is the opposite direction (Routes sending INTO
  // our provider) and shares action configurations rather than enabled state.
  // The count is the API's total, not the fetched page's length: the request is
  // capped at page_size, and a provider feeding 40 Routes must not be described
  // as feeding 19. `null` means the lookup failed — "no other Routes" is unknown,
  // so the confirmation takes its cautious wording instead.
  const providerRouteCountQuery = useQuery({
    ...providerRoutesQuery(token as string, routeProvider?.id ?? ''),
    enabled: !!routeProvider?.id && tokenUsable,
  });
  const providerAlsoProvidesFor: number | null = (() => {
    const data = providerRouteCountQuery.data;
    if (!data) return 0;
    if (!data.ok) return null;
    // Every Route of this provider except the one on screen. The one on
    // screen is by definition one of them, so it always comes off the
    // total — checking whether it appears in the fetched page would
    // over-count by one for a provider with more Routes than fit a page.
    const total = data.count ?? data.results.length;
    return Math.max(0, total - (defaultRouteId ? 1 : 0));
  })();

  // siteId -> route ids carrying data from that site back to our provider, the
  // other Routes that send data to our provider (action configurations live on
  // the integration record, so edits here are inherited by those flows too),
  // and each reverse Route's field mappings.
  const reverseSiteIds = useMemo(
    () => route.destinations.map((d) => d.id).sort(),
    [route.destinations],
  );
  const reverseQuery = useQuery({
    queryKey: [
      ...queryKeys.routes.reverse(routeProvider?.id ?? ''),
      reverseSiteIds,
    ],
    enabled: !!routeProvider?.id && tokenUsable,
    queryFn: async () => {
      const providerId = routeProvider.id;
      // Routes whose destination is OUR provider = candidate reverse flows.
      const data = await getReverseRoutes(token as string, providerId);
      const siteIds = new Set(reverseSiteIds);
      const map: Record<string, string[]> = {};
      const shared: { id: string; name: string }[] = [];
      // ?destination_id= selects Routes, not routing rules: a sender that also
      // routes elsewhere returns ALL of its routing rules. Deleting those to
      // turn one direction off would destroy unrelated flows, so keep only the
      // rules that actually route to this Route's provider.
      const senders = (data?.results || data || []).filter(
        (c: any) => c?.provider?.id,
      );
      const routesToProvider = async (rules: any[]) => {
        const kept: string[] = [];
        for (const rule of rules) {
          if (!rule?.id) continue;
          const declared = rule.destinations;
          if (Array.isArray(declared)) {
            if (declared.some((d: any) => (d?.id || d) === providerId))
              kept.push(rule.id);
            continue;
          }
          // The rule came back without its destinations — ask the Route.
          try {
            const ruleRoute = await getRouteConfiguration(
              token as string,
              rule.id,
            );
            const dests = ruleRoute?.destinations || [];
            if (dests.some((d: any) => (d?.id || d) === providerId))
              kept.push(rule.id);
          } catch (e) {
            console.log('could not resolve route destinations', rule.id, e);
          }
        }
        return kept;
      };
      for (const c of senders) {
        if (siteIds.has(c.provider.id)) {
          map[c.provider.id] = await routesToProvider(c.routing_rules || []);
        } else {
          // Anything else is an unrelated Route sharing this account.
          // Link by the provider INTEGRATION id — that's what route
          // URLs address. Still require a route record: a provider
          // with none has no Route page to link to.
          if (routeRecordId(c) && c.id)
            shared.push({ id: c.id, name: c.provider.name });
        }
      }
      // The reverse flows' field mappings live on those route records, so
      // fetch each one's configuration — the "in" arrow has nothing to
      // show (or to edit) without it. First route per site: that is the
      // one the enable/disable toggle created and addresses.
      const configs: ReverseConfigBySite = {};
      // Concurrently: one Route can have many bidirectional sites, and
      // serial fetches add a round-trip of latency each before any
      // incoming arrow can show its rules.
      await Promise.all(
        Object.entries(map).map(async ([siteId, ids]) => {
          const reverseId = ids[0];
          if (!reverseId) return;
          try {
            const reverseRoute = await getRouteConfiguration(
              token as string,
              reverseId,
            );
            configs[siteId] = {
              routeId: reverseId,
              name: reverseRoute?.configuration?.name ?? '',
              data: reverseRoute?.configuration?.data ?? { field_mappings: {} },
            };
          } catch (e) {
            console.log(
              'could not load reverse route configuration',
              reverseId,
              e,
            );
          }
        }),
      );
      return { map, shared, configs };
    },
  });
  const reverseRoutesBySite: Record<string, string[]> =
    reverseQuery.data?.map ?? EMPTY_REVERSE_MAP;
  const providerSharedWith: { id: string; name: string }[] =
    reverseQuery.data?.shared ?? EMPTY_SHARED;
  const reverseConfigBySite: ReverseConfigBySite =
    reverseQuery.data?.configs ?? EMPTY_REVERSE_CONFIGS;
  const refreshReverseRoutes = () =>
    queryClient.invalidateQueries({
      queryKey: queryKeys.routes.reverse(routeProvider.id),
    });

  // The provider and webhook tabs became sections of the flow map's provider
  // panel. Their URLs still exist in bookmarks, so send them to the equivalent
  // selection instead of rendering a tab that no longer exists.
  useEffect(() => {
    if (!route_id || !routeProvider?.id) return;
    if (effectiveSubpage !== 'provider' && effectiveSubpage !== 'webhook')
      return;
    navigate(`/routes/${route_id}`, {
      replace: true,
      state: {
        flowSelection: {
          kind: 'account',
          integrationId: routeProvider.id,
          section: effectiveSubpage === 'webhook' ? 'webhook' : undefined,
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route_id, routeProvider?.id, effectiveSubpage]);

  // Same URLs, but the provider never arrived: land on the flow map rather than
  // rendering a tab that no longer exists.
  useEffect(() => {
    if (!pageLoaded || !route_id || routeProvider?.id) return;
    if (effectiveSubpage !== 'provider' && effectiveSubpage !== 'webhook')
      return;
    navigate(`/routes/${route_id}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route_id, routeProvider?.id, effectiveSubpage]);

  // Arriving at the bare /routes/:id of an unhealthy Route opens Logs, because
  // that is where the reason lives. ONCE, on arrival — a standing rule would
  // make Configurations unreachable, since its tab sends the user back to this
  // same bare URL. After this fires the user drives, and the bare URL means
  // Configurations like it does everywhere else. The ref is per mount, and
  // RouteDetailsRoute keys the screen on :route_id, so a different Route gets
  // a fresh one.
  const unhealthyLandingSettled = useRef(false);
  useEffect(() => {
    if (unhealthyLandingSettled.current) return;
    // Wait for the real status: the placeholder Route reads healthy, and
    // latching on it would skip the redirect for every unhealthy Route.
    if (!pageLoaded || !route_id) return;
    unhealthyLandingSettled.current = true;
    // A caller that asked for a panel (or for the add-destination highlight)
    // has already chosen Configurations as surely as a named tab would have.
    // Landing on Logs would drop that request along with the state carrying
    // it — and an unhealthy Route is exactly the one a user is most likely
    // to be attaching a destination to.
    const askedForConfigurations =
      !!(location.state as any)?.flowSelection ||
      !!(location.state as any)?.highlightAddDestination;
    if (subpage || askedForConfigurations || route.status !== 'unhealthy')
      return;
    navigate(`/routes/${route_id}/logs`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageLoaded, route.status, subpage, route_id]);

  // The destinations and General (overview) tabs are gone too — the flow map
  // page carries both. Their URLs stay valid for bookmarks and land there.
  useEffect(() => {
    if (
      !route_id ||
      (effectiveSubpage !== 'destinations' && effectiveSubpage !== 'overview')
    )
      return;
    navigate(`/routes/${route_id}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route_id, effectiveSubpage]);

  // SELECT SUB PAGE ON ROUTE DETAILS PAGE
  function handleSelectSubPage(e: string) {
    // Configurations is what the bare URL shows, so it does not get a segment
    // of its own — otherwise the same tab would have two addresses.
    guardedLeave(() => navigate(routeTabPath(route_id, e)));
  }

  // HANDLE SAVE CHANGE METHODS
  // The Route's name is its own field on the route record — editing it must
  // not touch the provider Connection's name.
  function handleChangeRouteName(e: any) {
    setRouteName(e.target.value);
    setChangesMade(true);
  }

  function handleChangeRouteOwner(e: any) {
    setRouteProvider((prevProvider: Provider) => ({
      ...prevProvider,
      owner: { id: e.value, name: e.label, description: '' },
    }));
    setChangesMade(true);
  }

  function handleSearchOrgDropdown(e: any) {
    searchWorkspaces(e);
  }

  function handleLoadMoreOrgs() {
    clearWorkspaceSearch();
  }

  async function handleToggleProviderEnabled(e: any) {
    const updatedRoute = {
      enabled: e,
    };
    if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
      try {
        // A swallowed 401 comes back as undefined — not a save.
        const saved = await updateConnection(
          token,
          routeProvider.id,
          updatedRoute,
        );
        if (!saved) return;
        toast.success(`${t('Route updated')}`, {
          position: toast.POSITION.TOP_RIGHT,
        });
        setRouteProvider((prevProvider: Provider) => ({
          ...prevProvider,
          enabled: e,
        }));
        if (e) {
          trackRouteEnabled(
            routeProvider.id,
            routeProvider.type?.name,
            route.owner?.name,
          );
        } else {
          trackRouteDisabled(
            routeProvider.id,
            routeProvider.type?.name,
            route.owner?.name,
          );
        }
        setChangesMade(false);
      } catch (error) {
        console.log(error);
      }
    }
  }

  // `target` is the account the action belongs to — the flow map tests destination
  // accounts too, and an action must run against its own integration, not the
  // provider's. It defaults to the provider for screens that only show one account.
  const handleTestAction = async (
    action_id: string,
    action_type: string,
    target?: { id: string; configurations: any[] },
    formData?: Record<string, unknown>,
  ) => {
    if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
      setTestActionLoading(true);
      const account = target ?? {
        id: route.provider.id,
        configurations: routeProvider.configurations,
      };
      const stored = (account.configurations || []).find(
        (a: ConnectionConfiguration) => a.action.id === action_id,
      );
      const form_data = formData ?? stored?.data ?? {};

      try {
        let result = await testConnectionAction(
          token,
          account.id,
          action_type,
          { config_overrides: Object(form_data) },
        );
        if (result) {
          let { data: resp_data } = result;
          setTestActionData(resp_data);
        }
      } catch (err: any) {
        setTestActionData({
          error:
            err?.response?.data?.detail ||
            err?.message ||
            'Server error. Please try again.',
        });
      } finally {
        setTestActionLoading(false);
      }
    }
  };

  const formIsValid = () => {
    let form_errors: any = {};
    // validate the basic form
    if (routeName.trim().length === 0)
      form_errors.provider_name = t('Name is required.');
    if (!(routeProvider.owner?.name ?? route.owner.name))
      form_errors.provider_owner = t('Select a workspace.');

    setErrors(form_errors);
    return Object.keys(form_errors).length === 0;
  };

  async function handleUpdateRoute() {
    // The name goes to the route record; everything else still lives on the
    // provider integration. Renaming the Route never renames the Connection.
    const updatedProvider = {
      owner: routeProvider.owner?.id ?? route.owner.id,
      type: routeProvider.type.id,
      base_url: routeProvider.base_url,
      enabled: routeProvider.enabled,
    };
    // Keep changesMade set: the draft is still unsaved, and the Save/Cancel
    // row is what lets the user fix or abandon it.
    if (!formIsValid()) return;
    if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
      try {
        if (defaultRouteId && routeName.trim() !== routeDisplayName(route)) {
          await renameRoute(token, defaultRouteId, routeName.trim());
        }
        // Same as above: nothing back means nothing saved.
        const saved = await updateConnection(
          token,
          routeProvider.id,
          updatedProvider,
        );
        if (!saved) return;
        toast.success(`${t('Route updated')}`, {
          position: toast.POSITION.TOP_RIGHT,
        });
        // No navigation: the name lives in a bar above the flow map, and
        // navigating to the bare subpage dropped the configurations/*
        // selection, closing whichever panel the user had open.
        setChangesMade(false);
      } catch (error: any) {
        console.log(error);
        // Session expiry is handled by the axios interceptor + LoginModal; don't stack a toast on it.
        if (error?.response?.status === 401) return;
        toast.error(
          `${t('Something went wrong. Please try again or contact support.')}`,
          {
            position: toast.POSITION.TOP_RIGHT,
          },
        );
      }
    }
  }

  async function handleUpdateWebhookConfiguration(data: Record<string, any>) {
    // `webhook` comes back as the record on some responses and as a bare id
    // on others.
    const webhookRef = routeProvider.webhook_configuration?.webhook;
    const webhookId =
      (typeof webhookRef === 'string' ? webhookRef : webhookRef?.id) ||
      routeProvider.type?.webhook?.id;
    // Whatever stops the save has to say so, or Save looks dead.
    if (!webhookId) {
      toast.error(`${t('Failed to save webhook configuration')}`, {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }
    // No message for an expired session: the axios interceptor raises the
    // login modal, and calling it a webhook failure sends the user hunting
    // through their configuration.
    if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
      try {
        // A swallowed 401 comes back as undefined — not a save, so the
        // draft stays put. Same check as useConfigEditor's.
        const saved = await updateConnection(token, routeProvider.id, {
          webhook_configuration: {
            webhook: webhookId,
            data,
          },
        });
        if (!saved) return;
        toast.success(`${t('Connection updated')}`, {
          position: toast.POSITION.TOP_RIGHT,
        });
        setChangesMade(false);
      } catch (error) {
        console.log(error);
        toast.error(`${t('Failed to save webhook configuration')}`, {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    }
  }

  function handleCancelRouteChanges() {
    if (changesMade) {
      setShowConfirmationModal(true);
    } else {
      navigate('/');
      setChangesMade(false);
    }
  }

  const handleConfirmNavigation = () => {
    setShowConfirmationModal(false);
    setChangesMade(false);
    navigate('/');
  };

  const handleCancelNavigation = () => {
    setShowConfirmationModal(false);
  };

  // ADD AND REMOVE DESTINATIONS
  // Creating a destination for this Route. If exactly one technology filter is
  // active, preselect it so the user lands straight in configuration.
  const handleCreateDestination = () => {
    const preselect =
      destinationFilter.type__in.length === 1
        ? destinationFilter.type__in[0].id
        : undefined;
    navigate(
      `/routes/${route_id}/${effectiveSubpage}/newdestination`,
      preselect ? { state: { preselectTypeId: preselect } } : undefined,
    );
  };

  // Drawer path: attach the checked destinations, then select the first one on
  // the flow map so its node is the panel's subject.
  const handleAttachDestinationsFromDrawer = async (ids: string[]) => {
    if (ids.length === 0) return;
    if (!(token && typeof token === 'string' && !keycloak.isTokenExpired()))
      return;
    const added = drawerCandidates.filter((d: Destination) =>
      ids.includes(d.id),
    );
    // Local name: this is the route RECORD id the PATCHes target. It is never
    // the page's :route_id URL param, which is the provider integration id.
    const recordId = defaultRouteId;
    if (!recordId) {
      console.log(
        'cannot attach destinations: route has no default route',
        route.id,
      );
      toast.error(
        `${t('Something went wrong. Please try again or contact support.')}`,
        { position: toast.POSITION.TOP_RIGHT },
      );
      return;
    }
    const destination_ids = {
      destinations: Array.from(
        new Set([...destinations.map((d) => d.id), ...ids]),
      ),
    };
    try {
      const updated = await addRouteDestinations(
        token,
        recordId,
        destination_ids,
      );
      // A swallowed 401 resolves undefined — not an attach, so don't report one.
      if (!updated) return;
      added.forEach((d: Destination) =>
        trackDestinationAdded(route.id, d.type?.name, route.owner?.name),
      );
      toast.success(`${t('Route updated')}`, {
        position: toast.POSITION.TOP_RIGHT,
      });
      setAddDestinationDrawerOpen(false);
      // The URL keeps the id the page is addressed by (same rule as the
      // remove path), so attaching never relocates the user mid-flow.
      navigate(`/routes/${route_id}`, {
        replace: true,
        state: { flowSelection: { kind: 'account', integrationId: ids[0] } },
      });
    } catch (error) {
      console.log(error);
      toast.error(
        `${t('Something went wrong. Please try again or contact support.')}`,
        { position: toast.POSITION.TOP_RIGHT },
      );
    }
  };

  const handleRemoveDestination = async (dest: Destination) => {
    // The removed destination is NOT put back into the available list here.
    // Every surface that shows that list refetches it on open, so the server
    // is the source of truth — the optimistic push only ever produced stale
    // rows (and, before the dedupe, duplicate React keys) that flashed in the
    // drawer ahead of the real list and stole clicks meant for other rows.

    // remove dest from Route
    let tempArray: Destination[] = [];
    destinations.forEach((currentDest: Destination) => {
      if (dest.id !== currentDest.id) tempArray.push(currentDest);
      setDestinations(tempArray);
    });
    const destination_ids = {
      destinations: tempArray.map((a) => a.id),
    };
    setChangesMade(true);
    if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
      // The destinations list lives on the Route's underlying route, so without one
      // there is nothing to PATCH — say so instead of failing silently.
      // Local name: this is the route RECORD id the PATCHes target. It is
      // never the page's :route_id URL param, the provider integration id.
      const recordId = defaultRouteId;
      if (!recordId) {
        console.log(
          'cannot remove destination: route has no default route',
          route.id,
        );
        toast.error(
          `${t('Something went wrong. Please try again or contact support.')}`,
          { position: toast.POSITION.TOP_RIGHT },
        );
        setDestinations(destinations);
        setChangesMade(false);
        return;
      }
      // A destination that sends data back has its own reverse route, and the
      // arrows for it are drawn from route.destinations — detaching without
      // deleting it left that route running with nothing in this Route able to
      // stop it. Delete it first: if the PATCH below fails the user still sees
      // the destination, but nothing is silently pumping data.
      const reverseIds = reverseRoutesBySite[dest.id] || [];
      try {
        for (const reverseId of reverseIds) await deleteRoute(token, reverseId);
      } catch (err: any) {
        console.log(
          'failed to delete reverse route while removing destination',
          err,
        );
        if (err?.response?.status !== 401) {
          toast.error(
            `${t('Something went wrong. Please try again or contact support.')}`,
            { position: toast.POSITION.TOP_RIGHT },
          );
        }
        setDestinations(destinations);
        setChangesMade(false);
        return;
      }
      try {
        await addRouteDestinations(token, recordId, destination_ids);
        // Its field mapping is per flow, so it goes with the flow — otherwise
        // re-attaching the same destination silently revives the old rule.
        if (
          getRuleForDestination(
            routeConfigData,
            routeProvider?.id ?? '',
            dest.id,
          )
        ) {
          try {
            await updateRouteConfiguration(token, recordId, {
              name: routeConfigurationName || route.provider?.name || '',
              data: buildConfigWithoutRule(
                routeConfigData,
                routeProvider?.id ?? '',
                dest.id,
              ),
            });
          } catch (ruleErr) {
            // The destination is detached either way; a stale mapping is
            // not worth failing the removal over.
            console.log(
              "failed to drop the removed destination's rule",
              ruleErr,
            );
          }
        }
        toast.success(`${t('Route updated')}`, {
          position: toast.POSITION.TOP_RIGHT,
        });
        if (reverseIds.length > 0) refreshReverseRoutes();
        // The PATCH targets the route record; the URL keeps the provider
        // integration id the page is already addressed by. Routed through
        // routeTabPath so this cannot mint /routes/:id/configurations,
        // which the router answers with a redirect — the page would tear
        // down and rebuild, losing the selection the removal just set.
        navigate(routeTabPath(route_id, effectiveSubpage));
        setChangesMade(false);
      } catch (error) {
        console.log(error);
        toast.error(
          `${t('Something went wrong. Please try again or contact support.')}`,
          { position: toast.POSITION.TOP_RIGHT },
        );
        // Nothing was detached: put the destination back, like the
        // failures above, so the next attach or remove sends the real list.
        setDestinations(destinations);
        setChangesMade(false);
      }
    }
  };

  // SOURCE FILTERS (GUNDI-5178) — the whitelist/blacklist rules on this route's arrows.
  //
  // One request per route returns every rule; they are keyed here by destination and
  // direction, the pair a rule belongs to. Only the outgoing direction is offered for
  // now, but the key keeps the shape the reverse route would need.
  const [sourceFilters, setSourceFilters] = useState<
    Record<string, SourceFilter>
  >({});
  // Matches the backend's default page size for this sub-resource.
  const SOURCE_FILTER_PAGE_SIZE = 50;

  useEffect(() => {
    if (!defaultRouteId || !token || typeof token !== 'string') return;
    let cancelled = false;
    listRouteFilters(token, defaultRouteId)
      .then((filters) => {
        if (cancelled) return;
        const next: Record<string, SourceFilter> = {};
        filters.forEach((filter) => {
          next[sourceFilterKey(filter.destination.id, 'out')] = filter;
        });
        setSourceFilters(next);
      })
      .catch(() => {
        // Rendering "no rules" over a failed load invites recreating one into a 409.
        if (!cancelled)
          toast.error(
            `${t('Something went wrong. Please try again or contact support.')}`,
            { position: toast.POSITION.TOP_RIGHT },
          );
      });
    return () => {
      cancelled = true;
    };
  }, [defaultRouteId, token]);

  // The device list is replaced wholesale — there is no per-device endpoint — so the
  // draft carries the complete set and this sends it in one request.
  const handleSaveSourceFilter = async (draft: SourceFilterDraft) => {
    const site = rulesPanelDestination;
    // Source filters ship on the OUTGOING arrow only (frontend proposal, decision 4):
    // the reverse flow is a different Route record, and saving against defaultRouteId
    // here would filter the forward flow while claiming to filter the reverse one.
    // Resolving here would read as success to the panel (it closes the editor), so a
    // failed precondition toasts and THROWS — same reasoning as handleSaveRules.
    if (
      rulesDirection !== 'out' ||
      !site ||
      !defaultRouteId ||
      !token ||
      typeof token !== 'string'
    ) {
      toast.error(
        `${t('Something went wrong. Please try again or contact support.')}`,
        { position: toast.POSITION.TOP_RIGHT },
      );
      throw new Error('Source filter save preconditions not met');
    }
    const key = sourceFilterKey(site.id, rulesDirection);
    const existing = sourceFilters[key];
    const source_ids = draft.sources.map((source) => source.id);
    try {
      const saved = existing
        ? await updateRouteFilter(token, defaultRouteId, existing.id, {
            mode: draft.mode,
            source_ids,
          })
        : await createRouteFilter(token, defaultRouteId, {
            destination: site.id,
            mode: draft.mode,
            source_ids,
          });
      setSourceFilters((prev) => ({ ...prev, [key]: saved }));
      handleCloseRules();
    } catch (err: any) {
      // The backend answers 409 when the destination already carries a rule of
      // this type — which here means somebody saved one while this panel was open.
      toast.error(
        err?.response?.status === 409
          ? `${t('This destination already has a source rule. Reload the page to see it.')}`
          : `${t('Something went wrong. Please try again or contact support.')}`,
        { position: toast.POSITION.TOP_RIGHT },
      );
      throw err;
    }
  };

  const handleDeleteSourceFilter = async () => {
    const site = rulesPanelDestination;
    if (
      rulesDirection !== 'out' ||
      !site ||
      !defaultRouteId ||
      !token ||
      typeof token !== 'string'
    ) {
      toast.error(
        `${t('Something went wrong. Please try again or contact support.')}`,
        { position: toast.POSITION.TOP_RIGHT },
      );
      throw new Error('Source filter delete preconditions not met');
    }
    const key = sourceFilterKey(site.id, rulesDirection);
    const existing = sourceFilters[key];
    // Already gone (removed elsewhere): nothing left to delete, so the editor
    // has nothing to show either.
    if (!existing) {
      handleCloseRules();
      return;
    }
    try {
      await deleteRouteFilter(token, defaultRouteId, existing.id);
    } catch (err) {
      // The rule still exists — confirming the delete and dropping local state
      // would make it reappear on reload as a ghost.
      toast.error(
        `${t('Something went wrong. Please try again or contact support.')}`,
        { position: toast.POSITION.TOP_RIGHT },
      );
      throw err;
    }
    setSourceFilters((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    toast.success(`${t('Rule deleted')}`, {
      position: toast.POSITION.TOP_RIGHT,
    });
    handleCloseRules();
  };

  // Every page of the rule's devices, not just the first. Saving replaces the whole
  // list, so an editor opened on a truncated set would silently drop the rest.
  const loadSourceFilterSources = async (
    filterId: string,
  ): Promise<SourceSummary[]> => {
    if (!defaultRouteId || !token || typeof token !== 'string') {
      throw new Error("No route or session to load the rule's devices with");
    }
    const all: SourceSummary[] = [];
    let offset = 0;
    for (;;) {
      const page = await getRouteFilterSources(
        token,
        defaultRouteId,
        filterId,
        {
          limit: SOURCE_FILTER_PAGE_SIZE,
          offset,
        },
      );
      all.push(...page.results);
      // Advance by what actually arrived: the server may clamp pages below the
      // requested size, and a constant stride would skip the difference.
      offset += page.results.length;
      if (all.length >= page.count) return all;
      if (page.results.length === 0) {
        // Saving would replace the rule's whole list with this partial one,
        // silently dropping every device the pages never delivered.
        throw new Error("The rule's device list came back incomplete");
      }
    }
  };

  // Adding a device by hand from the Sources tab. The provider is the route's own,
  // the same one the rules editor writes against.
  const handleCreateRouteSource = async (payload: {
    external_id: string;
    name?: string;
  }) => {
    const provider = routeProvider?.id;
    // The drawer closes on resolve, so a quiet return here would discard the typing
    // while implying the device was created.
    if (!provider || !token || typeof token !== 'string') {
      toast.error(
        `${t('Something went wrong. Please try again or contact support.')}`,
        { position: toast.POSITION.TOP_RIGHT },
      );
      throw new Error('Source create preconditions not met');
    }
    await createSource(token, { provider, ...payload });
    toast.success(`${t('Source added')}`, {
      position: toast.POSITION.TOP_RIGHT,
    });
    routeSources.refresh();
  };

  // TRANSFORMATION RULES
  const handleOpenRules = (
    destination: Destination,
    direction: FlowDirectionKey,
    editor: 'device' | 'field_mapping',
  ) => {
    setRulesPanelDestination(destination);
    setRulesDirection(direction);
    setRulesInitialEditor(editor);
    setRulesEditorNonce((n) => n + 1);
    setRulesPanelOpen(true);
  };

  // Everything the rules panel needs about the flow it was opened for. The two
  // directions are two different route records with the provider/destination
  // roles swapped, so resolving them in one place keeps save, delete and the
  // panel's own display from disagreeing about which flow is being edited.
  const rulesTarget = useMemo(() => {
    const resolved = resolveRulesTarget({
      direction: rulesDirection,
      site: rulesPanelDestination,
      provider: routeProvider ?? route.provider,
      routeRecordId: defaultRouteId,
      routeConfigName: routeConfigurationName,
      routeConfigData: routeConfigData,
      reverseConfigBySite,
    });
    if (!resolved) return null;
    // The panel labels the other end of the flow, so it needs the record, not
    // just its id: the site going out, our provider coming back.
    const counterpart = (
      rulesDirection === 'out'
        ? rulesPanelDestination
        : (routeProvider ?? route.provider)
    ) as Destination;
    return { ...resolved, counterpart };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rulesPanelDestination,
    rulesDirection,
    defaultRouteId,
    routeProvider,
    routeConfigurationName,
    routeConfigData,
    reverseConfigBySite,
    route.provider,
  ]);

  // Where a saved/deleted rule lands: this Route's own configuration state, or
  // the cached configuration of the reverse Route.
  const storeRulesConfig = (data: FieldMappingsConfig, name?: string) => {
    const site = rulesPanelDestination;
    if (rulesDirection === 'out') {
      queryClient.setQueryData(
        queryKeys.routes.configuration(defaultRouteId ?? ''),
        (prev: { data: FieldMappingsConfig; name: string } | undefined) => ({
          data,
          name: name ?? prev?.name ?? '',
        }),
      );
      return;
    }
    if (!site) return;
    queryClient.setQueryData(
      [...queryKeys.routes.reverse(routeProvider.id), reverseSiteIds],
      (
        prev:
          | {
              map: Record<string, string[]>;
              shared: { id: string; name: string }[];
              configs: ReverseConfigBySite;
            }
          | undefined,
      ) => {
        const current = prev?.configs[site.id];
        if (!prev || !current) return prev;
        return {
          ...prev,
          configs: {
            ...prev.configs,
            [site.id]: { ...current, data, name: name ?? current.name },
          },
        };
      },
    );
  };

  const handleCloseRules = () => {
    setRulesPanelOpen(false);
  };

  // Unsaved edits in the open rules editor. Anything that takes the user off
  // this view asks first — the same Save-or-discard question the configuration
  // screens ask.
  const rulesPendingRef = useRef<PendingEdits | null>(null);
  const [leavePrompt, setLeavePrompt] = useState<SectionPrompt | null>(null);
  const rulesHaveEdits = () => !!rulesPendingRef.current?.hasEdits();
  const guardedLeave = (go: () => void) => {
    const pending = rulesPendingRef.current;
    if (!pending?.hasEdits()) return go();
    setLeavePrompt({
      onSave: () => pending.save(),
      onDiscard: () => pending.discard(),
      proceed: go,
    });
  };
  // Links outside this component (the top nav, breadcrumbs) can't call
  // guardedLeave, so in-app link clicks are caught on the way down, before
  // react-router's own handler. Plain left clicks only: a new-tab click
  // leaves this page as it is.
  const guardedLeaveRef = useRef(guardedLeave);
  guardedLeaveRef.current = guardedLeave;
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (!rulesHaveEdits()) return;
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      )
        return;
      const anchor = (event.target as Element | null)?.closest?.('a[href]');
      if (!anchor || anchor.getAttribute('target') === '_blank') return;
      const url = new URL((anchor as HTMLAnchorElement).href);
      if (url.origin !== window.location.origin) return;
      const to = `${url.pathname}${url.search}${url.hash}`;
      if (to === `${window.location.pathname}${window.location.search}`) return;
      event.preventDefault();
      event.stopPropagation();
      guardedLeaveRef.current(() => navigate(to));
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Reloading or closing the tab can only get the browser's own prompt.
  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!rulesHaveEdits()) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveRules = async (
    streamType: StreamType,
    mapping: FieldMapping,
    name: string,
  ): Promise<FieldErrors | void> => {
    // The record the PATCH targets is the flow's OWN route: this Route for the
    // outgoing direction, the reverse Route for the incoming one. Neither is
    // the page's :route_id URL param, which is the provider integration id.
    const recordId = rulesTarget?.recordId;
    const providerId = rulesTarget?.providerId;
    const destinationId = rulesTarget?.counterpart?.id;
    if (!recordId || !providerId || !destinationId) {
      // Field mappings live on the route's configuration, so with no route
      // there is nothing to save. Returning quietly reads as a dead button.
      console.log('cannot save rule', { recordId, providerId, destinationId });
      toast.error(`${t('Failed to save rule')}`, {
        position: toast.POSITION.TOP_RIGHT,
      });
      // Rejecting, not returning: a resolved save reads as "it landed" to the
      // panel and the leave-page prompt, which would then move on.
      throw new Error('Rule save preconditions not met');
    }
    if (!(token && typeof token === 'string' && !keycloak.isTokenExpired()))
      throw new Error('No session token');
    // buildConfigWithRule preserves any other keys (subject_type, ca_uuids, …) that
    // the route's configuration.data already carries, so the PATCH-replaces-data
    // semantics don't blow them away.
    const nextConfig = buildConfigWithRule(
      rulesTarget.data,
      providerId,
      destinationId,
      streamType,
      mapping,
    );
    try {
      await updateRouteConfiguration(token, recordId, {
        name,
        data: nextConfig,
      });
      storeRulesConfig(nextConfig, name);
      toast.success(`${t('Rule saved')}`, {
        position: toast.POSITION.TOP_RIGHT,
      });
      handleCloseRules();
    } catch (err: any) {
      // 400s carry field-level validation errors keyed by dotted path. We surface
      // them on the panel so the user knows what to fix; anything else falls back
      // to the generic save-failed toast.
      const responseStatus = err?.response?.status;
      if (responseStatus === 400 && err.response?.data) {
        const fieldErrors = parseServerErrors(
          err.response.data,
          providerId,
          destinationId,
          streamType,
        );
        if (Object.keys(fieldErrors).length > 0) return fieldErrors;
      }
      console.log(err);
      toast.error(`${t('Failed to save rule')}`, {
        position: toast.POSITION.TOP_RIGHT,
      });
      throw err;
    }
  };

  const handleDeleteRule = async () => {
    // Same resolution as the save above: the flow's own route record, with the
    // provider/destination roles as that direction sees them.
    const recordId = rulesTarget?.recordId;
    const providerId = rulesTarget?.providerId;
    const destinationId = rulesTarget?.counterpart?.id;
    if (!recordId || !providerId || !destinationId) {
      // Field mappings live on the route's configuration, so with no route
      // there is nothing to delete. Returning quietly reads as a dead button.
      console.log('cannot delete rule', {
        recordId,
        providerId,
        destinationId,
      });
      toast.error(`${t('Failed to delete rule')}`, {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }
    if (!(token && typeof token === 'string' && !keycloak.isTokenExpired()))
      return;
    try {
      const nextConfig = buildConfigWithoutRule(
        rulesTarget.data,
        providerId,
        destinationId,
      );
      const fieldMappingsEmpty =
        !nextConfig.field_mappings ||
        Object.keys(nextConfig.field_mappings).length === 0;
      const otherKeysEmpty =
        Object.keys(nextConfig).filter((k) => k !== 'field_mappings').length ===
        0;
      const isEffectivelyEmpty = fieldMappingsEmpty && otherKeysEmpty;

      if (isEffectivelyEmpty) {
        // Try the dedicated DELETE endpoint first so the backend doesn't keep an
        // empty row around. If it isn't deployed yet on the env we're hitting,
        // some infra returns the SPA's index.html for unknown API paths and our
        // apiUtils interceptor reads that as a Keycloak redirect — falsely
        // triggering the session-expired modal. To keep delete working until the
        // backend PR is merged we fall back to a PATCH with empty data on any
        // error other than a real auth failure.
        try {
          await deleteRouteConfiguration(token, recordId);
          storeRulesConfig({ field_mappings: {} }, '');
        } catch (deleteErr: any) {
          if (deleteErr?.response?.status === 401) throw deleteErr;
          console.log(
            'DELETE configuration not available, falling back to PATCH:',
            deleteErr,
          );
          await updateRouteConfiguration(token, recordId, {
            name: rulesTarget.configName,
            data: nextConfig,
          });
          storeRulesConfig(nextConfig);
        }
      } else {
        // Other rules / sibling keys remain — PATCH with the cleaned data.
        await updateRouteConfiguration(token, recordId, {
          name: rulesTarget.configName,
          data: nextConfig,
        });
        storeRulesConfig(nextConfig);
      }
      toast.success(`${t('Rule deleted')}`, {
        position: toast.POSITION.TOP_RIGHT,
      });
      handleCloseRules();
    } catch (err) {
      console.log(err);
      toast.error(`${t('Failed to delete rule')}`, {
        position: toast.POSITION.TOP_RIGHT,
      });
    }
  };

  const currentPanelRule = useMemo(() => {
    if (!rulesTarget?.providerId || !rulesTarget.counterpart?.id) return null;
    return getRuleForDestination(
      rulesTarget.data,
      rulesTarget.providerId,
      rulesTarget.counterpart.id,
    );
  }, [rulesTarget]);

  // IDs of destinations that currently have a transformation rule under this
  // provider. Drives the rule badge on the flow map's outgoing arrows.
  const destinationsWithRules = useMemo<Set<string>>(() => {
    const ids = new Set<string>();
    const providerId = routeProvider?.id;
    const providerEntry = providerId
      ? routeConfigData.field_mappings?.[providerId]
      : undefined;
    if (!providerEntry) return ids;
    for (const streamType of STREAM_TYPES) {
      const streamEntry = providerEntry[streamType];
      if (streamEntry) Object.keys(streamEntry).forEach((id) => ids.add(id));
    }
    return ids;
  }, [routeConfigData, routeProvider?.id]);

  // Same badge for the way back: the reverse Route's own configuration, where
  // the site is the provider and our provider is the destination.
  const reverseDestinationsWithRules = useMemo<Set<string>>(() => {
    const ids = new Set<string>();
    const providerId = routeProvider?.id;
    if (!providerId) return ids;
    Object.entries(reverseConfigBySite).forEach(([siteId, reverse]) => {
      const siteEntry = reverse.data?.field_mappings?.[siteId];
      if (!siteEntry) return;
      for (const streamType of STREAM_TYPES) {
        if (siteEntry[streamType]?.[providerId]) ids.add(siteId);
      }
    });
    return ids;
  }, [reverseConfigBySite, routeProvider?.id]);

  // Destinations carrying a device-filter rule. Forward direction only — the only
  // one the editor can save a filter for — and it feeds the same arrow badge.
  const destinationsWithDeviceFilters = useMemo<Set<string>>(() => {
    const ids = new Set<string>();
    Object.entries(sourceFilters).forEach(([key, filter]) => {
      if (!filter) return;
      const [destId, direction] = key.split('|');
      if (direction === 'out') ids.add(destId);
    });
    return ids;
  }, [sourceFilters]);

  // ── Bidirectional flows (GUNDI-5595) ────────────────────────────────────
  // The reverse direction is a second route: this Route's destination
  // becomes the data provider, and this Route's provider becomes its
  // destination. Both routes reuse the SAME integration records, and the UI
  // presents them as one bidirectional flow.
  const handleEnableReverseFlow = async (dest: Destination) => {
    if (!(token && typeof token === 'string' && !keycloak.isTokenExpired()))
      return;
    const ownerId = route.owner?.id || routeProvider.owner?.id;
    if (!ownerId) return;
    try {
      await createRoute(token, {
        // The list relies on this exact shape to spot the reverse Route (hideReverseRoutes).
        name: reverseRouteName(dest.name, routeProvider.name),
        owner: ownerId,
        data_providers: [dest.id],
        destinations: [routeProvider.id],
      });
      toast.success(`${t('Route updated')}`, {
        position: toast.POSITION.TOP_RIGHT,
      });
      refreshReverseRoutes();
    } catch (err: any) {
      console.log(err);
      if (err?.response?.status === 401) return;
      toast.error(
        `${t('Something went wrong. Please try again or contact support.')}`,
        { position: toast.POSITION.TOP_RIGHT },
      );
    }
  };

  const handleDisableReverseFlow = async (dest: Destination) => {
    if (!(token && typeof token === 'string' && !keycloak.isTokenExpired()))
      return;
    const routeIds = reverseRoutesBySite[dest.id] || [];
    if (routeIds.length === 0) {
      // The reverse flow exists (that is why the toggle is on) but we could not
      // resolve which route carries it. Deleting a guess could take out unrelated
      // Routes, so say so instead.
      console.log('no resolved reverse route to delete for', dest.id);
      toast.error(
        `${t('Something went wrong. Please try again or contact support.')}`,
        { position: toast.POSITION.TOP_RIGHT },
      );
      return;
    }
    try {
      for (const routeId of routeIds) await deleteRoute(token, routeId);
      toast.success(`${t('Route updated')}`, {
        position: toast.POSITION.TOP_RIGHT,
      });
      refreshReverseRoutes();
    } catch (err: any) {
      console.log(err);
      if (err?.response?.status === 401) return;
      toast.error(
        `${t('Something went wrong. Please try again or contact support.')}`,
        { position: toast.POSITION.TOP_RIGHT },
      );
    }
  };

  // SOURCE FILTER FUNCTIONS
  function handleChangeGlobalSourceSearch(searchSubStr: string) {
    setSourceFilter((prev: SourceFilterTypes) =>
      changeSourceGlobalSearch(searchSubStr, prev),
    );
    setSourcesOffset(0);
  }

  const handleSelectSourceUrlFilter = (selected: any) => {
    const tags = selected ? [{ name: selected.label, id: selected.value }] : [];
    setSourceFilter((prev: any) => ({ ...prev, destination_url__in: tags }));
    setSourcesOffset(0);
  };

  const toSingleSourceValue = (tags: { name: string; id: string }[]) =>
    tags.length > 0 ? { label: tags[0].name, value: tags[0].id } : null;

  const handleSortSources = (selected: { label: string; value: string }) => {
    setSourceFilter((prevFilter: any) => ({
      ...prevFilter,
      ordering: selected.value,
    }));
    setSourcesOffset(0);
  };

  const handleSourcePageClick = (e: any) => {
    setSourcesOffset(e.selected * page_size);
  };

  const skeletonBar = (w: string, h: string = 'h-4') => (
    <div className={`${h} ${w} ${skeletonClasses.base}`} />
  );

  // A type that carries a webhook is a webhook here even when it also has pull
  // config (kind would say "standard") — the same rule computeNeedsSetup and the
  // node panel apply, or the banner would count a missing payload parsing it
  // never shows a step for.
  const isWebhookRoute =
    routeProvider.type != null &&
    (getRouteKind(routeProvider.type) === 'webhook' ||
      !!routeProvider.type.webhook);
  // The record's presence isn't enough — ConfigurationsTab synthesizes one with
  // null data, and computeNeedsSetup requires .data too. Same rule, same answer.
  const webhookConfigured = !!routeProvider.webhook_configuration?.data;
  // Each Quick Start step reports on its own thing: you can attach a destination
  // before finishing the provider's credentials, so the checks are independent
  // rather than a sequence where reaching step three implies step two is done.
  // Three states, not two: a step the user hasn't reached yet is "upcoming", one
  // that is finished is "completed", and one whose thing exists but isn't
  // configured yet is "attention" — attaching a destination doesn't finish the
  // step if that destination still has required fields empty.
  // Only the Configurations tab reports pendingSetup, so on every other tab it
  // stays null — "not reported yet" must not be read as "not configured", or the
  // banner would claim nothing is done at all while sitting on Logs or Sources.
  const setupLoaded = pendingSetup !== null;
  const providerConfigured = !!pendingSetup && !pendingSetup.provider;
  const quickStartProviderStatus: StepStatus = !setupLoaded
    ? 'in-progress'
    : providerConfigured
      ? 'completed'
      : 'attention';
  const quickStartStepTwoStatus: StepStatus = !routeHasDestinations
    ? 'upcoming'
    : !setupLoaded || !pendingSetup.destinations
      ? 'completed'
      : 'attention';
  const quickStartWebhookStepStatus: StepStatus = webhookConfigured
    ? 'completed'
    : 'attention';
  // The banner exists only to remind the user what's left, so it renders only
  // while something is known to be missing — no destination yet, a webhook
  // route without its payload parsing, or the flow map reporting empty required
  // fields. Purely derived: it comes back the moment configuration goes missing
  // again, and a fully configured Route simply has no banner (no success step,
  // no dismiss memory).
  // GUNDI-5680 — TEMPORARY HACK: no Quick Start while InReach is the data
  // provider. NOTE for reviewers: this is an accepted, intentional TEMPORARY hack (GUNDI-5680) — not an oversight to flag. It is removed by the same schema/API work that retires tempExemptions.ts.
  // Hiding the whole banner (not just the affected steps) is a deliberate
  // product call for the interim. In the InReach ↔ EarthRanger flow EarthRanger is a data provider
  // too, but we don't need the user to configure its "pull events" and "pull
  // observations" parameters even though the schema marks them required — the
  // periodic pull tasks aren't used in this integration. EarthRanger only
  // streams the data received by the sensors API, using the API key generated
  // for that connection. Until the schemas (or the API) can say so, the banner
  // would nag about steps that don't apply, so it is hidden for InReach Routes.
  // Replace with a more elegant solution and drop this condition.
  const quickStartHiddenForInReach = routeProvider.type?.value === INREACH_TYPE;
  const quickStartHasWork =
    !quickStartHiddenForInReach &&
    (!routeHasDestinations ||
      (isWebhookRoute && !webhookConfigured) ||
      (!!pendingSetup && pendingSetup.total > 0));

  if (notFound || !isValidSubpage) return <NotFoundPage />;

  if (!pageLoaded)
    return (
      /* Same tint and bleed as the loaded page below, so the skeleton is not a
           white screen that turns teal the moment the data lands. */
      <div
        className="relative -mx-4 -mb-6 h-full min-h-[calc(100vh-80px)] bg-teal-t10 px-4 pb-6 md:-mx-8 md:min-h-[calc(100vh-70px)] md:px-8 desktop:-mx-[60px] desktop:px-[60px]"
        role="status"
        aria-busy="true"
      >
        <span className="sr-only">{t('Loading…')}</span>
        {/* Header skeleton */}
        <div className={routeDetailsClasses.header}>
          <div className="mx-auto max-w-list-table">
            <div className="mb-3 flex flex-row justify-between">
              <div className="flex w-3/4 flex-col gap-2">
                {skeletonBar('w-64', 'h-8')}
                <div className="mt-1 flex flex-row gap-2">
                  {skeletonBar('w-24')}
                  {skeletonBar('w-32')}
                </div>
              </div>
              {skeletonBar('w-40', 'h-8')}
            </div>
            {/* Tabs skeleton — General, Provider, Destinations, Sources, Logs */}
            <div className="flex flex-row gap-6 pt-1">
              {['w-16', 'w-16', 'w-24', 'w-16', 'w-10'].map((w, i) => (
                <div
                  key={i}
                  className={`${w} h-4 ${skeletonClasses.base} mb-3`}
                />
              ))}
            </div>
          </div>
        </div>
        {/* Content skeleton — varies by subpage */}
        {effectiveSubpage === 'configurations' ? (
          <div className="mx-auto mt-8 max-w-list-table px-0 md:px-4">
            {/* Flow map card */}
            <div className="mb-6 rounded-lg border border-teal-t30 bg-white">
              <div className="flex items-center gap-3 border-b border-unlisted-gray-lines px-5 py-3">
                {skeletonBar('w-28', 'h-5')}
                {skeletonBar('w-40', 'h-9 ml-auto')}
              </div>
              <div className="flex flex-col gap-6 px-3 py-8 md:flex-row md:items-center md:gap-16 md:px-5">
                {skeletonBar('w-full md:w-[260px]', 'h-[92px]')}
                <div className="flex w-full flex-col gap-4 md:w-auto">
                  {skeletonBar('w-full md:w-[260px]', 'h-[92px]')}
                  {skeletonBar('w-full md:w-[260px]', 'h-[92px]')}
                </div>
              </div>
            </div>
            {/* Selection panel */}
            <div className="rounded-lg border border-teal-t30 bg-white p-5">
              {['w-48', 'w-64', 'w-56'].map((w, i) => (
                <div key={i} className="mb-3">
                  {skeletonBar(w)}
                </div>
              ))}
            </div>
          </div>
        ) : effectiveSubpage === 'sources' ? (
          <div className="mx-auto mt-8 max-w-list-table">
            {skeletonBar('w-96 max-w-full', 'h-4 mb-6')}
            {/* Filter bar */}
            <div className="mb-3 flex flex-row flex-wrap gap-3">
              <div className="flex flex-col gap-1">
                {skeletonBar('w-12', 'h-3 mb-1')}
                {skeletonBar('w-48', 'h-9')}
              </div>
              <div className="flex flex-col gap-1">
                {skeletonBar('w-20', 'h-3 mb-1')}
                {skeletonBar('w-48', 'h-9')}
              </div>
            </div>
            {skeletonBar('w-32', 'h-4 mb-3')}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-unlisted-neutral-gray-100">
                    {['w-40', 'w-48', 'w-32'].map((w, i) => (
                      <th key={i} className="pb-3 pr-6 text-left">
                        <div className={`h-3 ${w} ${skeletonClasses.base}`} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: 8 }).map((_, i) => (
                    <tr
                      key={i}
                      className="border-b border-unlisted-neutral-gray-100"
                    >
                      {['w-44', 'w-52', 'w-36'].map((w, j) => (
                        <td key={j} className="py-3 pr-6">
                          <div className={`h-4 ${w} ${skeletonClasses.base}`} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="mx-auto mt-8 w-full max-w-list-table">
            <div className="flex w-full flex-col px-0 md:px-4">
              {/* A white card, like the panel this stands in for. */}
              <div className="rounded-lg border border-teal-t30 bg-white p-[16px]">
                {GENERAL_TAB_SKELETON_ROWS.map((row, i) => (
                  <div
                    key={i}
                    className="mb-[8px] border-b border-ui-divider-lines pb-[8px] pt-1 last:mb-0 last:border-b-0 last:pb-0"
                  >
                    <div className="flex w-full flex-row items-start justify-between gap-6">
                      <div className="flex w-1/2 flex-col gap-2">
                        {skeletonBar(row.label)}
                        {skeletonBar(row.desc, 'h-3')}
                      </div>
                      <div className="w-1/2 p-2">
                        {skeletonBar('w-full', 'h-9')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );

  return (
    <div>
      {showConfirmationModal && (
        <CancelChangesConfirmationModal
          onToggleModal={handleCancelNavigation}
          onConfirm={handleConfirmNavigation}
        />
      )}
      <div className="-mx-4 -mb-6 flex min-h-[calc(100vh-80px)] flex-col bg-teal-t10 px-4 pb-6 md:-mx-8 md:min-h-[calc(100vh-70px)] md:px-8 desktop:-mx-[60px] desktop:px-[60px]">
        {/* Direct children of this column, deliberately: RouteDetails
                    returns the title block plus a STICKY tab strip, and a
                    sticky element can only travel inside its own parent. */}
        <RouteDetails
          route={route}
          providerEnabled={routeProvider.enabled !== false}
          routeName={routeName}
          subPage={effectiveSubpage}
          onSelectSubPage={handleSelectSubPage}
        />
        {quickStartHasWork && (
          /* The progress bar is a wide horizontal stepper that can't fit on
                       small screens, so hide it below 780px and show it above.
                       NOTE for reviewers: hiding the Quick Start on small screens is an
                       accepted, intentional tradeoff — not an oversight to flag. */
          <div className="mx-auto mt-8 hidden w-full max-w-list-table px-0 md:px-4 min-[780px]:block">
            <RouteProgressIndicator
              stepZeroStatus="completed"
              stepOneStatus={quickStartProviderStatus}
              stepTwoStatus={quickStartStepTwoStatus}
              webhookStepStatus={
                isWebhookRoute ? quickStartWebhookStepStatus : undefined
              }
              pendingSetupCount={pendingSetup?.total ?? 0}
            />
          </div>
        )}
        <div className="mx-auto w-full max-w-list-table">
          {/* The Route's own settings sit above the flow map — the
                        General tab they lived on is gone. */}
          {effectiveSubpage === 'configurations' && (
            <div className="mt-8 px-0 md:px-4">
              <div className="rounded-lg border border-teal-t30 bg-white px-5 py-4">
                {/* Two columns per row: what the field is on the left,
                                    the field itself on the right — same layout as the
                                    account panels below. */}
                <div className="mb-2 flex w-full flex-col gap-2 border-b border-ui-divider-lines pb-3 md:flex-row md:items-start md:justify-between md:gap-6">
                  <div className="flex flex-col md:w-1/2">
                    <label htmlFor="route_name" className="mr-1">
                      {t('Route Name')} *
                    </label>
                    <span className="mt-1 text-sm text-ui-text-primary-text">
                      {t('A descriptive name to identify this Route.')}
                    </span>
                  </div>
                  <div className="md:w-1/2">
                    <input
                      id="route_name"
                      data-testid="ManageRouteDetails-route-name-input"
                      value={routeName}
                      onChange={(e) => handleChangeRouteName(e)}
                      className={`${inputClasses.standard} w-full`}
                    />
                    {(errors as any).provider_name && (
                      <p className="mt-1 text-sm text-unlisted-orange-600">
                        {(errors as any).provider_name}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex w-full flex-col gap-2 md:flex-row md:items-start md:justify-between md:gap-6">
                  <div className="flex flex-col md:w-1/2">
                    <label htmlFor="route_workspace" className="mr-1">
                      {t('Workspace')} *
                    </label>
                    <span className="mt-1 text-sm text-ui-text-primary-text">
                      {t('The workspace this Route belongs to.')}
                    </span>
                  </div>
                  <div className="md:w-1/2">
                    <SelectInput
                      inputId="route_workspace"
                      options={organizations.map((org: Organization) => ({
                        label: org.name,
                        value: org.id,
                      }))}
                      className={'basic-single'}
                      value={{
                        label:
                          routeProvider?.owner?.name ??
                          route?.owner?.name ??
                          '',
                        value:
                          routeProvider?.owner?.id ?? route?.owner?.id ?? '',
                      }}
                      isDisabled={false}
                      isLoading={false}
                      isClearable={false}
                      isSearchable={true}
                      isMulti={false}
                      name={t('Workspace')}
                      onChange={(e: any) => handleChangeRouteOwner(e)}
                      onMenuScrollToBottom={handleLoadMoreOrgs}
                      handleInputChange={handleSearchOrgDropdown}
                    />
                  </div>
                </div>
                {/* Also while an error is showing: hiding the row
                                    would strand an invalid draft with no way to
                                    save the correction or cancel out of it. */}
                {(changesMade || Object.keys(errors || {}).length > 0) && (
                  <div className="mt-4 flex items-center gap-3">
                    <button
                      className={buttonClasses.primaryAction}
                      onClick={handleUpdateRoute}
                      data-testid="ManageRouteDetails-save-general-button"
                    >
                      {t('Save')}
                    </button>
                    <button
                      className={buttonClasses.cancelAction}
                      onClick={() => {
                        setRouteName(routeDisplayName(route));
                        setErrors({});
                        requestRouteReload();
                        setChangesMade(false);
                      }}
                    >
                      {t('Cancel')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          {effectiveSubpage === 'configurations' && (
            <ConfigurationsTab
              route={route}
              routeProvider={routeProvider}
              token={
                token && typeof token === 'string' && !keycloak.isTokenExpired()
                  ? token
                  : undefined
              }
              currentMemberIsAdmin={currentMemberIsAdmin}
              onTestAction={handleTestAction}
              testActionLoading={testActionLoading}
              testActionResponse={testActionData}
              onSaveWebhook={handleUpdateWebhookConfiguration}
              onCancelWebhook={handleCancelRouteChanges}
              webhookChangesMade={changesMade}
              onWebhookChangesMade={() => setChangesMade(true)}
              reverseRoutesBySite={reverseRoutesBySite}
              providerCanReceive={
                !!routeProvider.type?.actions?.some(
                  (a: any) => a.type === 'push',
                )
              }
              destinationsWithRules={destinationsWithRules}
              reverseDestinationsWithRules={reverseDestinationsWithRules}
              destinationsWithDeviceFilters={destinationsWithDeviceFilters}
              getRule={(destId: string) =>
                getRuleForDestination(
                  routeConfigData,
                  routeProvider?.id ?? '',
                  destId,
                )
              }
              // The reverse flow's rule lives on its own route record, and
              // its provider is the site — the roles are swapped.
              getReverseRule={(destId: string) =>
                getRuleForDestination(
                  reverseConfigBySite[destId]?.data,
                  destId,
                  routeProvider?.id ?? '',
                )
              }
              reverseRuleEditable={(destId: string) =>
                !!reverseConfigBySite[destId]?.routeId
              }
              providerSharedWith={providerSharedWith}
              providerAlsoProvidesFor={providerAlsoProvidesFor}
              onEnableReverseFlow={handleEnableReverseFlow}
              onDisableReverseFlow={handleDisableReverseFlow}
              onOpenRules={handleOpenRules}
              getSourceFilter={(destId: string, direction: string) =>
                sourceFilters[sourceFilterKey(destId, direction)] ?? null
              }
              onRemoveSite={async (d: any) => {
                await handleRemoveDestination(d);
                requestRouteReload();
              }}
              onAddDestination={openAddDestinationDrawer}
              onNeedsSetupChange={setPendingSetup}
              onToggleRouteEnabled={handleToggleProviderEnabled}
              routeUrlId={route_id}
              organizations={organizations}
              onLoadMoreOrgs={handleLoadMoreOrgs}
              onSearchOrgs={handleSearchOrgDropdown}
              highlightAddDestination={
                !!(location.state as any)?.highlightAddDestination
              }
            />
          )}
          {effectiveSubpage === 'sources' && (
            <RouteDetailsSource
              sources={routeSources.results}
              totalSources={routeSources.count}
              globalSearchStr={sourceFilter.search}
              take={page_size}
              offset={sourcesOffset}
              onPageClicked={handleSourcePageClick}
              pageLoaded={pageLoaded}
              loadingSources={routeSources.loading}
              destinationUrls={destinationUrls}
              loadingDestinationUrls={loadingDestinationUrls}
              destinationUrlFilterValue={toSingleSourceValue(
                sourceFilter.destination_url__in,
              )}
              onChangeGlobalSourceSearch={handleChangeGlobalSourceSearch}
              onRefresh={() => routeSources.refresh()}
              onChangeDestinationUrlFilter={handleSelectSourceUrlFilter}
              onViewDestination={(destinationId: string) =>
                navigate(`/routes/${route_id}`, {
                  replace: true,
                  state: {
                    flowSelection: {
                      kind: 'account',
                      integrationId: destinationId,
                    },
                  },
                })
              }
              ordering={sourceFilter.ordering}
              onCreateSource={handleCreateRouteSource}
              onChangeSortBy={handleSortSources}
            />
          )}
          {effectiveSubpage === 'logs' && (
            <RouteDetailsLog
              {...logsTab}
              pageLoaded={pageLoaded}
              // Unhealthy Route (the flow map's red state): logs open
              // pre-filtered to Warning minimum so the signal is on top.
              defaultMinimumWarning={route.status === 'unhealthy'}
            />
          )}
        </div>
      </div>
      <AddDestinationDrawer
        isOpen={addDestinationDrawerOpen}
        onClose={() => setAddDestinationDrawerOpen(false)}
        // Now that provider-side records are listed as destinations, the
        // Route's own provider can turn up here — attaching it would
        // point the Route at itself.
        candidates={drawerCandidates}
        loading={drawerList.loading}
        onAttach={handleAttachDestinationsFromDrawer}
        onCreateNew={() => {
          setAddDestinationDrawerOpen(false);
          handleCreateDestination();
        }}
        onSearch={(searchStr: string) => {
          setDestinationFilter((prev: FilterTypes) => ({
            ...prev,
            search: searchStr,
          }));
          setDrawerOffset(0);
        }}
        totalCount={drawerList.count}
        pageOffset={drawerOffset}
        onPageChange={handleDestinationsPageClick}
        workspaceOptions={organizations
          .filter((o: Organization) => o.id)
          .map((o: Organization) => ({ label: o.name, value: o.id as string }))}
        workspaceFilter={drawerWorkspaceFilter}
        onChangeWorkspaceFilter={handleDrawerWorkspaceChange}
        loadingWorkspaces={loadingOrganizations}
      />
      <TransformationRulesPanel
        isOpen={rulesPanelOpen}
        onClose={handleCloseRules}
        destination={rulesTarget?.counterpart ?? null}
        providerId={rulesTarget?.providerId ?? ''}
        currentRule={currentPanelRule}
        currentConfigurationName={rulesTarget?.configName ?? ''}
        onSave={handleSaveRules}
        onDelete={handleDeleteRule}
        currentSourceFilter={
          rulesPanelDestination
            ? (sourceFilters[
                sourceFilterKey(rulesPanelDestination.id, rulesDirection)
              ] ?? null)
            : null
        }
        onSaveSourceFilter={handleSaveSourceFilter}
        onDeleteSourceFilter={handleDeleteSourceFilter}
        onLoadSourceFilterSources={loadSourceFilterSources}
        initialEditor={rulesInitialEditor}
        initialEditorNonce={rulesEditorNonce}
        pendingEditsRef={rulesPendingRef}
      />
      <UnsavedSectionPrompt
        prompt={leavePrompt}
        onClose={() => setLeavePrompt(null)}
      />
    </div>
  );
}
